import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DataTable from './DataTable';
import { Modal, Button, PageHeader } from './UI';
import { useCRUD } from '../hooks/useCRUD';
import ConfirmModal from './ConfirmModal';
import Breadcrumb from './Breadcrumb';
import EmptyState from './EmptyState';

export default function GenericCRUDPage({
  title, subtitle, service, entityName, columns,
  FormFields, defaultValues = {}, modalSize = 'md', extraActions
}) {
  const crud = useCRUD(service, entityName);
  const form = useForm({ defaultValues });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    crud.fetchData();
  }, [crud.page, crud.search]); // eslint-disable-line

  useEffect(() => {
    if (crud.editItem) form.reset(crud.editItem);
    else form.reset(defaultValues);
  }, [crud.editItem]); // eslint-disable-line

  const onSubmit = (data) => crud.handleSave(data);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await service.delete(deleteTarget._id);
      const toastMod = await import('react-hot-toast');
      toastMod.default.success(entityName + ' deleted');
      crud.fetchData();
    } catch (e) {
      const toastMod = await import('react-hot-toast');
      toastMod.default.error('Failed to delete ' + entityName);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <Breadcrumb />
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={<Button onClick={crud.openCreate}><Plus size={16} /> Add {entityName}</Button>}
      />

      {!crud.loading && crud.data.length === 0 ? (
        <EmptyState
          title={'No ' + entityName + 's found'}
          message={'Start by adding your first ' + entityName + '.'}
          action={crud.openCreate}
          actionLabel={'Add ' + entityName}
        />
      ) : (
        <DataTable
          columns={columns}
          data={crud.data}
          loading={crud.loading}
          totalItems={crud.total}
          page={crud.page}
          pageSize={20}
          onPageChange={crud.setPage}
          onSearch={crud.setSearch}
          title={title}
          exportFilename={title.toLowerCase().replace(/\s+/g, '_')}
          actions={(row) => (
            <>
              {extraActions && extraActions(row)}
              <button onClick={() => crud.openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors" title="Edit">
                <Pencil size={15} />
              </button>
              <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                <Trash2 size={15} />
              </button>
            </>
          )}
        />
      )}

      <Modal isOpen={crud.modalOpen} onClose={crud.closeModal} title={crud.editItem ? 'Edit ' + entityName : 'Add ' + entityName} size={modalSize}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormFields form={form} editItem={crud.editItem} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={crud.closeModal}>Cancel</Button>
            <Button type="submit" loading={crud.saving}>{crud.editItem ? 'Update' : 'Save'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        title={'Delete ' + entityName}
        message={'Are you sure you want to delete "' + (deleteTarget?.name || deleteTarget?.commercialName || entityName) + '"? This action cannot be undone.'}
      />
    </div>
  );
}
